import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { ENTITY_TYPE } from 'common/constants';
import { usePaginationPreference } from 'hooks/usePaginationPreference';
import { useDialog } from 'common/DialogProvider';
import { HRApiWrapper } from 'api/HRApiWrapper';
import WorkExperienceDrawer from './WorkExperienceDrawer';

interface Props {
  employeeId: number;
  hrApi: HRApiWrapper;
}

const WorkExperienceSection: React.FC<Props> = ({ employeeId, hrApi }) => {
  const toast = useToastMessageQueue();
  const { openDialog, closeDialog } = useDialog();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [errors] = useState({});
  const [paginationModel, setPaginationModel] = usePaginationPreference('hrWorkExp', 10);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrApi.getWorkExperienceByEmployee('1', employeeId);
      const data: any[] = res.data?.data ?? [];
      data.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
      setItems(data);
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { fetch(); }, [employeeId]);

  const handleDelete = (id: number) => {
    const dialogId = openDialog({
      title: intl.get('delete.modal.title'),
      message: intl.get('delete.modal.roleRow.message'),
      type: 'confirm',
      confirmButton: {
        confirmLabel: intl.get('delete.modal.delete.button'),
        handleConfirm: async () => {
          try { await hrApi.deleteWorkExperience('1', id); fetch(); }
          catch (error) { handleAPIError(error, toast, errors); }
          finally { closeDialog(dialogId); }
        },
      },
      cancelButton: {
        cancelLabel: intl.get('delete.modal.cancel.button'),
        handleCancel: () => closeDialog(dialogId),
      },
    });
  };

  const columns: GridColDef[] = [
    { field: 'companyName', headerName: intl.get('hr.workExp.company'), flex: 1 },
    { field: 'role', headerName: intl.get('hr.workExp.role'), flex: 1 },
    {
      field: 'startDate', headerName: intl.get('hr.workExp.startDate'), width: 130,
      valueFormatter: (v: any) => v ? new Date(v).toLocaleDateString() : '',
    },
    {
      field: 'endDate', headerName: intl.get('hr.workExp.endDate'), width: 130,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString() : intl.get('hr.workExp.present'),
    },
    {
      field: 'actions', headerName: '', width: 120, sortable: false,
      renderCell: (params) => (
        <DefaultActions row={params.row} entityType={ENTITY_TYPE.EMPLOYEE}
          onEdit={() => { setSelected(params.row); setDrawerOpen(true); }}
          onDelete={() => handleDelete(params.row.id)}
          editScopes={['recruitment.manage']} deleteScopes={['recruitment.manage']} />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{intl.get('hr.workExp.title')}</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDrawerOpen(true); }}>
          {intl.get('hr.workExp.add')}
        </Button>
      </Box>
      <Box style={{ width: '100%', height: 320 }}>
        <DataGrid
          rows={items}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25]}
          disableRowSelectionOnClick
        />
      </Box>

      <WorkExperienceDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={async (data) => {
          if (selected) {
            await hrApi.updateWorkExperience('1', { ...data, id: selected.id });
          } else {
            await hrApi.createWorkExperience('1', { ...data, idEmployee: employeeId });
          }
          setDrawerOpen(false);
          fetch();
        }}
        item={selected}
      />
    </Box>
  );
};

export default WorkExperienceSection;
